import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  CircularProgress,
  makeStyles
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import FileCopyIcon from "@material-ui/icons/FileCopy";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const TIPOS_SUPORTE = [
  { id: "suporte_comum", nome: "Suporte Comum" },
  { id: "suporte_desenvolvimento", nome: "Suporte Desenvolvimento" },
  { id: "implemnetacao_sistema", nome: "Sugestão de implementação" }
];

const useStyles = makeStyles((theme) => ({
  mainContainer: { padding: theme.spacing(3) },
  filterPaper: { padding: theme.spacing(2), marginBottom: theme.spacing(3) },
  
  // 🟢 AJUSTADO: Linha da tabela interativa e linha selecionada
  tableRow: { 
    cursor: "pointer", 
    "&:hover": { backgroundColor: theme.palette.action.hover },
    transition: "background-color 0.2s ease"
  },
  selectedRow: {
    backgroundColor: "#e3f2fd !important", // Destaque azul claro
    borderLeft: "4px solid #1976d2" // Barra azul na borda esquerda para guiar o olhar
  },

  // 🟢 NOVO: Containers com scroll e altura fixa no tamanho da tela
  tableScrollContainer: {
    maxHeight: "calc(100vh - 280px)",
    overflowY: "auto"
  },
  detailPaper: { 
    padding: theme.spacing(3), 
    maxHeight: "calc(100vh - 280px)",
    overflowY: "auto" 
  },

  badgePendente: { backgroundColor: "#fff3e0", color: "#e65100", fontWeight: "bold" },
  badgeEmAndamento: { backgroundColor: "#e3f2fd", color: "#0d47a1", fontWeight: "bold" },
  badgeConcluido: { backgroundColor: "#e8f5e9", color: "#1b5e20", fontWeight: "bold" },
  badgeCancelado: { backgroundColor: "#ffebee", color: "#c62828", fontWeight: "bold" },
  actionButtons: { display: "flex", gap: theme.spacing(1), marginTop: theme.spacing(2), justifyContent: "flex-end" },
  devSection: {
    backgroundColor: "#f5f5f5",
    padding: theme.spacing(2),
    borderRadius: 4,
    borderLeft: "4px solid #1976d2",
    marginTop: theme.spacing(2)
  }
}));

export default function SuporteTickets() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);

  // Separação de termos de busca (filtro vs modal)
  const [filterSearchParam, setFilterSearchParam] = useState("");
  const [modalSearchParam, setModalSearchParam] = useState("");

  const [contactsOptions, setContactsOptions] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadedContactsCache, setLoadedContactsCache] = useState([]);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Filtros
  const [filterId, setFilterId] = useState("");
  const [filterTitulo, setFilterTitulo] = useState("");
  const [filterTipo, setFilterTipo] = useState("");
  const [filterAnalista, setFilterAnalista] = useState("");
  const [filterCliente, setFilterCliente] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDataCriacao, setFilterDataCriacao] = useState("");
  const [filterDataConclusao, setFilterDataConclusao] = useState("");

  // Formulário
  const [formTicket, setFormTicket] = useState({
    id: null,
    titulo: "",
    tipo: "suporte_comum",
    userId: "",
    contactId: "",
    descricao: "",
    status: "pendente",
    ticketDev: "",
    respostaDev: "",
    contact: null
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: usersData } = await api.get("/users");
        setUsers(usersData.users || usersData);

        const { data: ticketsSuporte } = await api.get("/suporte-tickets");
        setTickets(ticketsSuporte);

        const { data: contactsData } = await api.get("/contacts", {
          params: { searchParam: "", pageNumber: 1, limit: 100 }
        });
        setLoadedContactsCache(contactsData.contacts || contactsData);
      } catch (err) {
        toastError(err);
      }
    };
    fetchData();
  }, []);

  // Busca de contatos unificada para filtro e modal
  useEffect(() => {
    const activeSearch = openModal ? modalSearchParam : filterSearchParam;

    if (!activeSearch || activeSearch.length < 3) {
      setLoadingContacts(false);
      return;
    }

    setLoadingContacts(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/contacts", {
            params: { searchParam: activeSearch },
          });
          const fetchedContacts = data.contacts || data;
          setContactsOptions(fetchedContacts);

          setLoadedContactsCache(prev => {
            const merged = [...prev];
            fetchedContacts.forEach(c => {
              if (!merged.find(m => m.id === c.id)) merged.push(c);
            });
            return merged;
          });

          setLoadingContacts(false);
        } catch (err) {
          setLoadingContacts(false);
          toastError(err);
        }
      };

      fetchContacts();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [filterSearchParam, modalSearchParam, openModal]);

  const handleSaveTicket = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        const { data } = await api.put(`/suporte-tickets/${formTicket.id}`, formTicket);
        setTickets(tickets.map(t => t.id === formTicket.id ? data : t));
        setSelectedTicket(data);
      } else {
        const { data } = await api.post("/suporte-tickets", formTicket);
        setTickets([data, ...tickets]);
        setSelectedTicket(data);
      }
      setOpenModal(false);
      setModalSearchParam("");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDuplicateClick = async (ticket) => {
    try {
      const { data } = await api.post("/suporte-tickets", {
        titulo: `${ticket.titulo} - Cópia`,
        tipo: ticket.tipo,
        userId: ticket.userId,
        contactId: ticket.contactId,
        descricao: ticket.descricao,
        status: "pendente",
        ticketDev: ticket.ticketDev,
        respostaDev: ticket.respostaDev
      });
      setTickets([data, ...tickets]);
      setSelectedTicket(data);
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm("Deseja deletar este ticket de suporte?")) {
      try {
        await api.delete(`/suporte-tickets/${id}`);
        setTickets(tickets.filter(t => t.id !== id));
        setSelectedTicket(null);
      } catch (err) {
        toastError(err);
      }
    }
  };

  const handleOpenCreateModal = () => {
    setIsEditing(false);
    setFormTicket({
      id: null,
      titulo: "",
      tipo: "suporte_comum",
      userId: user?.id || "",
      contactId: "",
      descricao: "",
      status: "pendente",
      ticketDev: "",
      respostaDev: "",
      contact: null
    });
    setModalSearchParam("");
    setContactsOptions([]);
    setOpenModal(true);
  };

  const handleOpenEditModal = (ticket) => {
    setIsEditing(true);

    // Clona o ticket e garante que o contactId exista
    setFormTicket({ 
      ...ticket,
      contactId: ticket.contactId || ticket.contact?.id || "" 
    });

    // Injeta o contato do ticket como opção inicial no modal para não abrir em branco
    if (ticket.contact) {
      setContactsOptions([ticket.contact]);
    } else if (ticket.contactId) {
      const currentContact = loadedContactsCache.find(c => Number(c.id) === Number(ticket.contactId));
      setContactsOptions(currentContact ? [currentContact] : []);
    } else {
      setContactsOptions([]);
    }

    setModalSearchParam("");
    setOpenModal(true);
  };

  const getUserName = (id) => users.find(u => u.id === Number(id))?.name || "Sem analista";
  
  const getContactName = (ticket) => {
    if (!ticket) return "Sem cliente";
    
    // 1. Tenta pegar direto do objeto contact retornado pela API
    if (ticket.contact && ticket.contact.name) {
      return ticket.contact.name;
    }

    // 2. Se for uma string/objeto direto ou fallback pelo cache
    const cId = ticket.contactId || (typeof ticket === "number" || typeof ticket === "string" ? ticket : null);
    if (cId) {
      const found = loadedContactsCache.find(c => Number(c.id) === Number(cId));
      if (found) return found.name;
    }

    return "Sem cliente";
  };

  const getTipoNome = (id) => TIPOS_SUPORTE.find(t => t.id === id)?.nome || "Não definido";

  const getStatusBadge = (status) => {
    switch (status) {
      case "em_andamento": return classes.badgeEmAndamento;
      case "concluido": return classes.badgeConcluido;
      case "cancelado": return classes.badgeCancelado;
      default: return classes.badgePendente;
    }
  };

  const getStatusLabel = (status) => {
    if (status === "em_andamento") return "EM ANDAMENTO";
    return status ? status.toUpperCase() : "PENDENTE";
  };

  const filteredTickets = tickets.filter(t => {
    const matchesId = t.id.toString().includes(filterId);
    const matchesTitulo = t.titulo.toLowerCase().includes(filterTitulo.toLowerCase());
    const matchesTipo = filterTipo === "" || t.tipo === filterTipo;
    const matchesAnalista = filterAnalista === "" || t.userId === Number(filterAnalista);
    const matchesCliente = filterCliente === "" || t.contactId === Number(filterCliente);
    const matchesStatus = filterStatus === "" || t.status === filterStatus;
    const matchesDataCriacao = filterDataCriacao === "" || (t.createdAt && t.createdAt.startsWith(filterDataCriacao));
    const matchesDataConclusao = filterDataConclusao === "" || (t.concludedAt && t.concludedAt.startsWith(filterDataConclusao));

    return matchesId && matchesTitulo && matchesTipo && matchesAnalista && matchesCliente && matchesStatus && matchesDataCriacao && matchesDataConclusao;
  });

  return (
    <div className={classes.mainContainer}>
      <Grid container justifyContent="space-between" alignItems="center" style={{ marginBottom: 20 }}>
        <Typography variant="h5" color="primary" style={{ fontWeight: "bold" }}>
          Painel de Atendimento - Suporte
        </Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenCreateModal}>
          Novo Chamado
        </Button>
      </Grid>

      {/* FILTROS DA GRID */}
      <Paper className={classes.filterPaper} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={1}>
            <TextField label="ID" variant="outlined" fullWidth size="small" value={filterId} onChange={e => setFilterId(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField label="Pesquisar Título" variant="outlined" fullWidth size="small" value={filterTitulo} onChange={e => setFilterTitulo(e.target.value)} />
          </Grid>

          <Grid item xs={12} sm={2}>
            <Autocomplete
              options={contactsOptions}
              loading={loadingContacts}
              getOptionLabel={(option) => option.name || ""}
              value={loadedContactsCache.find(c => c.id === Number(filterCliente)) || null}
              onChange={(event, newValue) => {
                setFilterCliente(newValue ? newValue.id : "");
              }}
              onInputChange={(e, value) => setFilterSearchParam(value)}
              getOptionSelected={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente"
                  variant="outlined"
                  size="small"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingContacts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              select
              label="Tipo de Suporte"
              variant="outlined"
              fullWidth
              size="small"
              value={filterTipo}
              onChange={e => setFilterTipo(e.target.value)}
            >
              <MenuItem value="">Todos os Tipos</MenuItem>
              {TIPOS_SUPORTE.map(tipo => (
                <MenuItem key={tipo.id} value={tipo.id}>{tipo.nome}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              select
              label="Analista"
              variant="outlined"
              fullWidth
              size="small"
              value={filterAnalista}
              onChange={e => setFilterAnalista(e.target.value)}
            >
              <MenuItem value="">Todos os Analistas</MenuItem>
              {users.map(u => (
                <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField select label="Status" variant="outlined" fullWidth size="small" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <MenuItem value="">Todos os Status</MenuItem>
              <MenuItem value="pendente">Pendente</MenuItem>
              <MenuItem value="em_andamento">Em andamento</MenuItem>
              <MenuItem value="concluido">Concluído</MenuItem>
              <MenuItem value="cancelado">Cancelado</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label="Data de Criação"
              type="date"
              variant="outlined"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filterDataCriacao}
              onChange={e => setFilterDataCriacao(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label="Data de Conclusão"
              type="date"
              variant="outlined"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={filterDataConclusao}
              onChange={e => setFilterDataConclusao(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* TABELA E DETALHES */}
      <Grid container spacing={3}>
  <Grid item xs={12} md={7}>
    {/* 🟢 ADICIONADO A CLASSE DE SCROLL AQUI */}
    <TableContainer component={Paper} variant="outlined" className={classes.tableScrollContainer}>
      <Table size="medium" stickyHeader> {/* 💡 'stickyHeader' deixa o cabeçalho fixo no topo ao rolar */}
        <TableHead>
          <TableRow>
            <TableCell style={{ fontWeight: "bold" }}>ID</TableCell>
            <TableCell style={{ fontWeight: "bold" }}>Assunto / Chamado</TableCell>
            <TableCell style={{ fontWeight: "bold" }}>Cliente</TableCell>
            <TableCell style={{ fontWeight: "bold" }}>Tipo</TableCell>
            <TableCell style={{ fontWeight: "bold" }}>Analista</TableCell>
            <TableCell style={{ fontWeight: "bold" }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredTickets.map(ticket => {
            // 🟢 VERIFICA SE O TICKET ATUAL É O QUE ESTÁ SELECIONADO
            const isSelected = selectedTicket?.id === ticket.id;

            return (
              <TableRow 
                key={ticket.id} 
                className={`${classes.tableRow} ${isSelected ? classes.selectedRow : ""}`} 
                onClick={() => setSelectedTicket(ticket)}
              >
                <TableCell style={{ fontWeight: "bold" }}>{ticket.id}</TableCell>
                <TableCell>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>{ticket.titulo}</Typography>
                </TableCell>
                <TableCell>{getContactName(ticket)}</TableCell>
                <TableCell>{getTipoNome(ticket.tipo)}</TableCell>
                <TableCell>{getUserName(ticket.userId)}</TableCell>
                <TableCell>
                  <Chip label={getStatusLabel(ticket.status)} size="small" className={getStatusBadge(ticket.status)} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  </Grid>

  {/* LADO DIREITO (DETALHES) PERMANECE AQUI */}
  <Grid item xs={12} md={5}>
    <Paper className={classes.detailPaper} variant="outlined">
            {selectedTicket ? (
              <div>
                <Typography variant="h6" color="primary" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  Chamado #{selectedTicket.id}
                  <Chip label={getStatusLabel(selectedTicket.status)} className={getStatusBadge(selectedTicket.status)} />
                </Typography>
                <Divider style={{ margin: "12px 0" }} />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">Assunto do Chamado</Typography>
                    <Typography variant="body1" style={{ fontWeight: 600 }}>{selectedTicket.titulo}</Typography>
                    <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
                      Criado em: {new Date(selectedTicket.createdAt).toLocaleString()} {selectedTicket.concludedAt && ` | Concluído em: ${new Date(selectedTicket.concludedAt).toLocaleString()}`}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">Cliente / Contato</Typography>
                    <Typography variant="body1" style={{ fontWeight: 550, color: "#333" }}>{getContactName(selectedTicket)}</Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Tipo de Suporte</Typography>
                    <Typography variant="body2" style={{ fontWeight: 550 }}>{getTipoNome(selectedTicket.tipo)}</Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Analista Atribuído</Typography>
                    <Typography variant="body2">{getUserName(selectedTicket.userId)}</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">Descrição do Problema</Typography>
                    <Paper style={{ padding: 12, backgroundColor: "#fafafa" }} variant="outlined">
                      <Typography variant="body2" style={{ whiteSpace: "pre-line" }}>{selectedTicket.descricao || "Nenhuma descrição fornecida."}</Typography>
                    </Paper>
                  </Grid>

                  {(selectedTicket.ticketDev || selectedTicket.respostaDev) && (
                    <Grid item xs={12}>
                      <div className={classes.devSection}>
                        <Typography variant="subtitle2" style={{ fontWeight: "bold", color: "#0d47a1" }}>
                          Integração com Desenvolvimento
                        </Typography>
                        {selectedTicket.ticketDev && (
                          <Typography variant="body2" style={{ marginTop: 4 }}>
                            <strong>Ticket de Dev:</strong> {selectedTicket.ticketDev}
                          </Typography>
                        )}
                        {selectedTicket.respostaDev && (
                          <Typography variant="body2" style={{ marginTop: 8, fontStyle: "italic" }}>
                            <strong>Resposta do Dev:</strong> "{selectedTicket.respostaDev}"
                          </Typography>
                        )}
                      </div>
                    </Grid>
                  )}
                </Grid>

                <Divider style={{ margin: "20px 0 10px 0" }} />
                <div className={classes.actionButtons}>
                  <IconButton color="default" title="Duplicar Chamado" onClick={() => handleDuplicateClick(selectedTicket)}>
                    <FileCopyIcon />
                  </IconButton>
                  <IconButton color="primary" title="Editar Chamado" onClick={() => handleOpenEditModal(selectedTicket)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton color="secondary" title="Excluir Chamado" onClick={() => handleDeleteClick(selectedTicket.id)}>
                    <DeleteIcon />
                  </IconButton>
                </div>
              </div>
            ) : (
              <Grid container justifyContent="center" alignItems="center" style={{ height: "100%", minHeight: 250, color: "#999" }}>
                <Typography align="center">Selecione um chamado na lista para ver os detalhes e ações.</Typography>
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* MODAL DE CADASTRO / EDIÇÃO */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSaveTicket}>
          <DialogTitle style={{ fontWeight: "bold" }}>
            {isEditing ? `Editar Chamado #${formTicket.id}` : "Abrir Novo Chamado de Suporte"}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Assunto / Chamado"
                  variant="outlined"
                  fullWidth
                  required
                  size="small"
                  value={formTicket.titulo}
                  onChange={e => setFormTicket({ ...formTicket, titulo: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  options={contactsOptions}
                  loading={loadingContacts}
                  getOptionLabel={(option) => option?.name || ""}
                  value={
                    contactsOptions.find(c => Number(c.id) === Number(formTicket.contactId)) || 
                    loadedContactsCache.find(c => Number(c.id) === Number(formTicket.contactId)) || 
                    formTicket.contact || 
                    null
                  }
                  onChange={(event, newValue) => {
                    setFormTicket({ 
                      ...formTicket, 
                      contactId: newValue ? newValue.id : "",
                      contact: newValue || null
                    });
                  }}
                  onInputChange={(e, value) => setModalSearchParam(value)}
                  getOptionSelected={(option, value) => option?.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente / Contato"
                      variant="outlined"
                      size="small"
                      fullWidth
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loadingContacts ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  select
                  label="Tipo de Suporte"
                  variant="outlined"
                  fullWidth
                  required
                  size="small"
                  value={formTicket.tipo}
                  onChange={e => setFormTicket({ ...formTicket, tipo: e.target.value })}
                >
                  {TIPOS_SUPORTE.map(tipo => (
                    <MenuItem key={tipo.id} value={tipo.id}>{tipo.nome}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  select
                  label="Analista Técnico"
                  variant="outlined"
                  fullWidth
                  required
                  size="small"
                  value={formTicket.userId}
                  onChange={e => setFormTicket({ ...formTicket, userId: Number(e.target.value) })}
                >
                  {users.map(u => (
                    <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Descrição do Problema"
                  variant="outlined"
                  fullWidth
                  required
                  multiline
                  rows={4}
                  value={formTicket.descricao}
                  onChange={e => setFormTicket({ ...formTicket, descricao: e.target.value })}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label="Ticket ID (Desenvolvimento)"
                  variant="outlined"
                  fullWidth
                  size="small"
                  placeholder="Ex: DEV-402"
                  value={formTicket.ticketDev || ""}
                  onChange={e => setFormTicket({ ...formTicket, ticketDev: e.target.value })}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  select
                  label="Status do Chamado"
                  variant="outlined"
                  fullWidth
                  required
                  size="small"
                  value={formTicket.status}
                  onChange={e => setFormTicket({ ...formTicket, status: e.target.value })}
                >
                  <MenuItem value="pendente">Pendente</MenuItem>
                  <MenuItem value="em_andamento">Em andamento</MenuItem>
                  <MenuItem value="concluido">Concluído</MenuItem>
                  <MenuItem value="cancelado">Cancelado</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Resposta do Desenvolvimento"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Notas, soluções ou atualizações do time de dev..."
                  value={formTicket.respostaDev || ""}
                  onChange={e => setFormTicket({ ...formTicket, respostaDev: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)} color="secondary">Cancelar</Button>
            <Button type="submit" variant="contained" color="primary">Confirmar</Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}